/**
 * Streak Service - Manages user learning streaks and daily engagement
 * 
 * Logic:
 * - Streak increments if activity is performed on consecutive days
 * - Streak stays same if activity is on the same day
 * - Streak resets to 1 if a day is missed
 */

import { supabase } from '@/config/supabase';

export interface UserStreak {
  streak_count: number;
  last_activity_at: string | null;
  best_streak: number;
}

export class StreakService {
  /**
   * Update user streak based on activity
   * Should be called when a user completes a day or generates a roadmap
   */
  static async updateStreak(email: string): Promise<UserStreak | null> {
    try {
      // Get current streak info
      const { data: profile, error } = await supabase
        .from('user_profiles')
        .select('streak_count, last_activity_at, best_streak')
        .eq('email', email)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
      
      let streakCount = profile?.streak_count || 0;
      let bestStreak = profile?.best_streak || 0;
      let lastActivityAt = profile?.last_activity_at ? new Date(profile.last_activity_at) : null;
      let lastActivityDate = lastActivityAt 
        ? new Date(lastActivityAt.getFullYear(), lastActivityAt.getMonth(), lastActivityAt.getDate()).getTime()
        : 0;

      const diffDays = Math.floor((today - lastActivityDate) / (1000 * 60 * 60 * 24));

      if (diffDays === 0 && profile) {
        // Already active today, no change to streak
        return { streak_count: streakCount, last_activity_at: profile.last_activity_at, best_streak: bestStreak };
      } else if (diffDays === 1) {
        // Active yesterday, increment streak
        streakCount += 1;
      } else {
        // Missed a day or first activity, reset to 1
        streakCount = 1;
      }

      if (streakCount > bestStreak) {
        bestStreak = streakCount;
      }

      const updatedData = {
        streak_count: streakCount,
        last_activity_at: now.toISOString(),
        best_streak: bestStreak,
        updated_at: now.toISOString(),
      };

      const { error: updateError } = await supabase
        .from('user_profiles')
        .update(updatedData)
        .eq('email', email);

      if (updateError) throw updateError;

      return {
        streak_count: streakCount,
        last_activity_at: updatedData.last_activity_at,
        best_streak: bestStreak
      };
    } catch (error) {
      console.error('Error updating streak:', error);
      return null;
    }
  }

  /**
   * Get current streak info without updating
   */
  static async getStreak(email: string): Promise<UserStreak | null> {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('streak_count, last_activity_at, best_streak')
        .eq('email', email)
        .single();

      if (error) return null;
      
      // Check if streak is broken (more than 1 day since last activity)
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
      const lastActivityAt = data.last_activity_at ? new Date(data.last_activity_at) : null;
      const lastActivityDate = lastActivityAt 
        ? new Date(lastActivityAt.getFullYear(), lastActivityAt.getMonth(), lastActivityAt.getDate()).getTime()
        : 0;
      
      const diffDays = Math.floor((today - lastActivityDate) / (1000 * 60 * 60 * 24));
      
      if (diffDays > 1) {
        return { streak_count: 0, last_activity_at: data.last_activity_at, best_streak: data.best_streak };
      }

      return {
        streak_count: data.streak_count || 0,
        last_activity_at: data.last_activity_at,
        best_streak: data.best_streak || 0
      };
    } catch (error) {
      return null;
    }
  }
}
